import { type FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { Spinner } from "@via/components/loading/loading";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import {
  useCreateOrUpdatePipeline,
  useCreatePipelineMenuGroup,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { createEmptyPipeline } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { createPipelineOptions } from "@via/utils/form-validators.tsx";
import {
  NewPipelineRequestBody,
  SelectOption,
} from "@via/utils/interfaces.tsx";

interface CreatePipelineFormProps {
  menuGroups: SelectOption[];
  setOpen: (open: boolean) => void;
}

const CreatePipelineForm: FC<CreatePipelineFormProps> = ({
  menuGroups,
  setOpen,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [options, setOptions] = useState(menuGroups);
  const [selected, setSelected] = useState<SelectOption | null>(null);
  const navigate = useNavigate();
  const { resetChangeCount } = useChangesDetect();

  const {
    mutate: createOrRevisePipeline,
    data: newPipelineData,
    isSuccess: isNewPipelineSuccess,
    isPending: isCreatePipelineLoading,
  } = useCreateOrUpdatePipeline();

  const {
    mutateAsync: createNewMenuGroup,
    data: menuGroupData,
    isSuccess: isMenuGroupsSuccess,
    isPending: isMenuGroupsLoading,
  } = useCreatePipelineMenuGroup();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<NewPipelineRequestBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      summary: "",
    },
  });

  useEffect(() => {
    if (newPipelineData) {
      navigate(`/pipelines/${newPipelineData.id}`);
      setOpen(false);
    }
  }, [isNewPipelineSuccess]);

  useEffect(() => {
    if (menuGroups) {
      setOptions(menuGroups);
    }
  }, [menuGroups]);

  useEffect(() => {
    if (menuGroupData) {
      const { name, id } = menuGroupData;
      const newMenuGroup = { label: name, value: id };
      setSelected(newMenuGroup);
      setOptions(prev => [...prev, newMenuGroup]);
    }
  }, [isMenuGroupsSuccess]);

  const handleCreatePipeline = (payload: NewPipelineRequestBody) => {
    const modified = { ...payload, menuGroup: selected?.value };
    const submission = createEmptyPipeline(modified);
    createOrRevisePipeline(submission);
    resetChangeCount();
  };

  const onSubmit = handleSubmit(handleCreatePipeline);

  const handleCreatePipelineMenuGroup = async (name: string) => {
    const result = await createNewMenuGroup({ name });
    if (result) {
      setValue("menuGroup", result.id);
    }
  };

  const handleSelectionChange = (option: any) => {
    setSelected(option);
    setValue("menuGroup", option?.value as number);
  };

  return (
    <>
      <form
        ref={formRef}
        onSubmit={e => {
          e.preventDefault();
          onSubmit(e);
        }}
        className="py-4">
        <label className="mb-2 block text-sm font-medium text-gray-900">
          Choose a Menu Group <span className="text-red-400">*</span>
        </label>
        <SelectComponent
          control={control}
          name="menuGroup"
          rules={createPipelineOptions.menuGroup}
          options={options}
          value={selected}
          isLoading={isMenuGroupsLoading || isCreatePipelineLoading}
          isCreatable
          noOptionsMessage={"No results available, type to create one"}
          onChange={handleSelectionChange}
          onCreateOption={handleCreatePipelineMenuGroup}
          placeholder={"Select or Create a Menu Group"}
        />
        {errors.menuGroup && (
          <span className="text-xs text-rose-500">
            {errors.menuGroup.message}
          </span>
        )}

        <FormInputComponent
          control={control}
          name="name"
          label="Pipeline Name"
          placeholder="Enter Pipeline Name"
          validations={createPipelineOptions.name}
          showRequiredField
        />

        <FormTextAreaComponent
          control={control}
          name="summary"
          label="Pipeline Summary"
          placeholder="Describe your Pipeline here"
          rows={5}
        />
      </form>
      <div className="modal-footer flex justify-end space-x-2">
        <button
          id="create-pipeline-modal-confirm-button"
          data-testid="modalConfirmButton"
          type="submit"
          form={formRef.current?.id}
          onClick={() => formRef.current?.requestSubmit()}
          disabled={isCreatePipelineLoading}
          className="via-modal inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600">
          <Spinner loading={isCreatePipelineLoading}>Create</Spinner>
        </button>
        <button
          id="create-pipeline-modal-cancel-button"
          data-testid="modalCancelButton"
          type="button"
          onClick={() => setOpen(false)}
          className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-gray-600 hover:bg-gray-500 focus-visible:outline-gray-600">
          Cancel
        </button>
      </div>
    </>
  );
};

export default CreatePipelineForm;
